import { Button, Stack } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import React, { Dispatch, FC, SetStateAction } from "react";
import { Attack, Menstruation, SuspectedHeadacheType } from "../../types";
import * as apiClient from "../../api";
import { DateTime } from "luxon";
import HeadacheAttacks from "./HeadacheAttacks";
import Menstruations from "./Menstruations";

const byStartDate = (
  { startDate: a }: { startDate: string },
  { startDate: b }: { startDate: string },
) => DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis();

const byStartDateTime = (
  { startDateTime: a }: { startDateTime: string },
  { startDateTime: b }: { startDateTime: string },
) => DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis();

interface PatientHeadacheDiaryTabProps {
  patientId: string;
  attacks: Attack[];
  setAttacks: Dispatch<SetStateAction<Attack[]>>;
  attacksLoaded: boolean;
  menstruations: Menstruation[];
  setMenstruations: Dispatch<SetStateAction<Menstruation[]>>;
  menstruationsLoaded: boolean;
}

const PatientHeadacheDiaryTab: FC<PatientHeadacheDiaryTabProps> = ({
  patientId,
  attacks,
  setAttacks,
  attacksLoaded,
  menstruations,
  setMenstruations,
  menstruationsLoaded,
}) => {
  const addAttack = async ({
    startDateTime,
    endDateTime,
    awake,
    disabledForTwoHours,
    suspectedHeadacheType,
    notes,
  }: {
    startDateTime: DateTime;
    endDateTime: DateTime;
    awake: boolean;
    disabledForTwoHours: boolean;
    suspectedHeadacheType: SuspectedHeadacheType;
    notes: string;
  }) => {
    const requestBody = {
      startDateTime: startDateTime.toISO()!,
      endDateTime: endDateTime.toISO()!,
      awake,
      disabledForTwoHours,
      suspectedHeadacheType,
      notes,
    };
    const res = await apiClient.addAttack(patientId, requestBody);
    const newAttack = { attackId: res.data.attackId, ...requestBody };
    const updatedAttacks = [...attacks, newAttack].sort(byStartDateTime);
    setAttacks(updatedAttacks);
  };

  const editAttack = (
    attackId: number,
    {
      startDateTime,
      endDateTime,
      awake,
      disabledForTwoHours,
      suspectedHeadacheType,
      notes,
    }: {
      startDateTime: DateTime;
      endDateTime: DateTime;
      awake: boolean;
      disabledForTwoHours: boolean;
      suspectedHeadacheType: SuspectedHeadacheType;
      notes: string;
    },
  ) => {
    const requestBody = {
      startDateTime: startDateTime.toISO()!,
      endDateTime: endDateTime.toISO()!,
      awake,
      disabledForTwoHours,
      suspectedHeadacheType,
      notes,
    };
    apiClient.editAttack(patientId, attackId, requestBody);

    const updatedAttacks = attacks.map((attack) =>
      attack.attackId === attackId
        ? ({ ...attack, ...requestBody } as Attack)
        : attack,
    );
    setAttacks(updatedAttacks);
  };

  const deleteAttack = (attackId: number) => {
    apiClient.deleteAttack(patientId, attackId);
    const updatedAttacks = attacks.filter(
      (attack) => attack.attackId !== attackId,
    );
    setAttacks(updatedAttacks);
  };

  const addMenstruation = async ({
    startDate,
    endDate,
  }: {
    startDate: DateTime;
    endDate: DateTime;
  }) => {
    const res = await apiClient.addMenstruation(patientId, {
      startDate: startDate.toFormat("dd/MM/yyyy"),
      endDate: endDate.toFormat("dd/MM/yyyy"),
    });
    const newMenstruation = {
      menstruationId: res.data.menstruationId,
      startDate: startDate.toISODate(),
      endDate: endDate.toISODate(),
    } as Menstruation;

    const updatedMenstruations = [...menstruations, newMenstruation].sort(
      byStartDate,
    );
    setMenstruations(updatedMenstruations);
  };

  const editMenstruation = (
    menstruationId: number,
    {
      startDate,
      endDate,
    }: {
      startDate: DateTime;
      endDate: DateTime;
    },
  ) => {
    apiClient.editMenstruation(patientId, menstruationId, {
      startDate: startDate.toFormat("dd/MM/yyyy"),
      endDate: endDate.toFormat("dd/MM/yyyy"),
    });

    const updatedMenstruations = menstruations
      .map((menstruation) =>
        menstruation.menstruationId === menstruationId
          ? ({
              ...menstruation,
              startDate: startDate.toISODate(),
              endDate: endDate.toISODate(),
            } as Menstruation)
          : menstruation,
      )
      .sort(byStartDate);

    setMenstruations(updatedMenstruations);
  };

  const deleteMenstruation = (menstruationId: number) => {
    apiClient.deleteMenstruation(patientId, menstruationId);
    const updatedMenstruations = menstruations.filter(
      (menstruation) => menstruation.menstruationId !== menstruationId,
    );
    setMenstruations(updatedMenstruations);
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await apiClient.downloadDiaryPdf(patientId);

      // Create a Blob from the response data
      const blob = new Blob([res.data], { type: "application/pdf" });

      // Create a URL for the Blob
      const pdfUrl = URL.createObjectURL(blob);

      // Open the PDF in a new tab or window
      window.open(pdfUrl, "_blank");
    } catch (err) {
      console.error("Error fetching PDF:", err);
    }
  };

  return (
    <Stack spacing={2}>
      <HeadacheAttacks
        attacksLoaded={attacksLoaded}
        attacks={attacks}
        addAttack={addAttack}
        editAttack={editAttack}
        deleteAttack={deleteAttack}
      />
      <Menstruations
        menstruationsLoaded={menstruationsLoaded}
        menstruations={menstruations}
        addMenstruation={addMenstruation}
        editMenstruation={editMenstruation}
        deleteMenstruation={deleteMenstruation}
      />
      <Button
        variant="contained"
        startIcon={<DownloadIcon />}
        onClick={handleDownloadPdf}
      >
        Download headache diary
      </Button>
    </Stack>
  );
};

export default PatientHeadacheDiaryTab;
